import { useState, RefObject } from 'react';

type FullscreenValue = true | false | 'not-supported';

export const useFullscreenStatus = (elRef: RefObject<HTMLElement>): [FullscreenValue, () => void] => {
  let setFullscreen = (): void => {
    if (elRef.current == null) return;

    requestFullscreen(elRef.current).catch(() => {
      setIsFullscreen(false);
    });
  };

  let isFullScreenSupported: FullscreenValue;

  try {
    isFullScreenSupported = getBrowserFullscreenElement() !== null;
    const intervalId = setInterval(() => {
      try {
        const isElementFullscreen = !!getBrowserFullscreenElement();
        if (isElementFullscreen !== isFullscreen) {
          setIsFullscreen(isElementFullscreen);
        }
      } catch (ex) {
        clearInterval(intervalId);
        setIsFullscreen(false);
      }
    }, 500);
  } catch (e) {
    isFullScreenSupported = 'not-supported';
    setFullscreen = () => {};
  }

  const [isFullscreen, setIsFullscreen] = useState<FullscreenValue>(isFullScreenSupported);

  return [isFullscreen, setFullscreen];
};

function getBrowserFullscreenElement() {
  return (document as any)[getBrowserFullscreenElementProp()];
}

function getBrowserFullscreenElementProp() {
  if (typeof document.fullscreenElement !== 'undefined') {
    return 'fullscreenElement';
  } else if (typeof (document as any).mozFullScreenElement !== 'undefined') {
    return 'mozFullScreenElement';
  } else if (typeof (document as any).msFullscreenElement !== 'undefined') {
    return 'msFullscreenElement';
  } else if (typeof (document as any).webkitFullscreenElement !== 'undefined') {
    return 'webkitFullscreenElement';
  } else {
    throw new Error('fullscreenElement is not supported by this browser');
  }
}

function requestFullscreen(elem: HTMLElement): Promise<void> {
  if (elem.requestFullscreen) {
    return elem.requestFullscreen();
  } else if ((elem as any).webkitRequestFullscreen) {
    /* Safari */
    return new Promise((resolve: () => void) => {
      (elem as any).webkitRequestFullscreen();
      return Promise.resolve();
    });
  } else if ((elem as any).msRequestFullscreen) {
    return new Promise((resolve: () => void) => {
      (elem as any).msRequestFullscreen();
      return Promise.resolve();
    });
  }

  return Promise.reject();
}
